import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Platform } from '@app/classes/platform.class';
import { FeatureFlag } from '@app/enums';
import {
    EmployeeAccessGuard,
    EmployeeModuleGuard,
    FeatureGuard,
    HasFeatureRedirectGuard,
    NavigationGuard,
} from '@app/guards';
import { AdminGuard } from '@app/guards/admin.guard';
import { PermissionGuard } from '@app/guards/permission.guard';
import { RoutesWithOptions } from '@app/interfaces';
import { EmployeesResolver } from '@employees/employees.resolver';
import { EmployeeGuard } from '@employees/guards';
import {
    AccountShowView,
    BenefitsShowView,
    CompensationStoreView,
    CompensationUpdateView,
    DepartmentIndexView,
    DepartmentShowView,
    DocumentsIndexView,
    EmployeeAssetsIndexView,
    EmployeeIndexView,
    EmployeeLeaveOfAbsence,
    EmployeeRehireView,
    EmployeeStoreView,
    EmployeeTasksIndexView,
    EmployeeTasksStoreView,
    EmployeeTerminateView,
    JobAndPayShowView,
    JourneyIndexView,
    MyTeamView,
    OffboardingIndexView,
    OffboardingTaskIndexView,
    OffboardingTaskTemplatesStoreView,
    OffboardingTaskTemplatesView,
    OffboardingTasksStoreView,
    OnboardingBankingInfoView,
    OnboardingIndexView,
    OnboardingTaskIndexView,
    OnboardingTaskTemplatesStoreView,
    OnboardingTaskTemplatesView,
    OnboardingTasksStoreView,
    OrganizationAccordionView,
    PaystubIndexView,
    PerformanceIndexView,
    PersonalShowView,
    RecordsIndexView,
    RemindersIndexView,
    SurveyIndexView,
    TaskTemplatesIndexView,
    TaskTemplatesShowView,
    TaskTemplatesStoreView,
    TasksIndexView,
    TerminatedEmployeeStoreView,
    TimeOffShowViewV3,
    TimeTrackingShow,
    TrainingIndexView,
} from '@employees/views';
import { OrganizationChartV2View } from '@employees/views/organization/chartv2/organization-chart-v2.view';
import { TrainingProgram } from '@models/training/training-program.model';
import { HireEmployee } from '@settings/meta/module-feature-permissions.meta';

/** Documents */
import { EmployeeDocumentsBulkUploadView } from '@employees/views/employee/documents-v2/bulk-upload/employee-documents-bulk-upload.view';

const routes: RoutesWithOptions = [
    {
        path: '',
        redirectTo: 'directory',
        pathMatch: 'full',
    },
    // Departments
    {
        path: 'departments',
        component: DepartmentIndexView,
        canActivate: [PermissionGuard],
        data: {
            permission: [...Platform.people.departments.permission.view],
            canActivateRedirectTo: ['/employees/organization'],
        },
    },
    {
        path: 'departments/:department',
        component: DepartmentShowView,
        resolve: {
            models: EmployeesResolver,
        },
    },
    // Directory
    {
        path: 'directory',
        children: [
            {
                path: '',
                component: EmployeeIndexView,
                canActivate: [PermissionGuard],
                data: {
                    permission: [...Platform.people.directory.permission.view],
                    canActivateRedirectTo: ['/employees/team'],
                },
            },
            {
                path: 'create',
                component: EmployeeStoreView,
            },
            {
                path: 'create-terminated',
                component: TerminatedEmployeeStoreView,
            },
            {
                path: ':employee',
                canActivate: [EmployeeGuard],
                resolve: {
                    models: EmployeesResolver,
                },
                runGuardsAndResolvers: 'always',
                children: [
                    {
                        path: '',
                        redirectTo: 'personal',
                        pathMatch: 'full',
                    },
                    {
                        path: 'personal',
                        component: PersonalShowView,
                    },
                    {
                        path: 'job-and-pay',
                        component: JobAndPayShowView,
                    },
                    {
                        path: 'create-compensation',
                        component: CompensationStoreView,
                    },
                    {
                        path: 'edit-compensation/:salary',
                        component: CompensationUpdateView,
                    },
                    {
                        path: 'documents',
                        component: DocumentsIndexView,
                        canActivate: [HasFeatureRedirectGuard, PermissionGuard],
                        data: {
                            permission: [...Platform.people.employeeDocuments.permission.view],
                            canActivateRedirectTo: ['..'],
                            feature: {
                                flags: [FeatureFlag.documentsV2],
                                redirectTo: ['/employees', 'directory', ':employee', 'documents-v2'],
                            },
                        },
                    },
                    {
                        path: 'documents/bulk-upload',
                        component: EmployeeDocumentsBulkUploadView,
                        canActivate: [HasFeatureRedirectGuard, PermissionGuard],
                        canDeactivate: [NavigationGuard],
                        data: {
                            isAccountView: false,
                            permission: [...Platform.people.employeeDocuments.permission.view],
                            canActivateRedirectTo: ['..'],
                            feature: {
                                flags: [FeatureFlag.documentsV2],
                                redirectTo: ['/employees', 'directory', ':employee', 'documents-v2', 'bulk-upload'],
                            },
                        },
                    },
                    {
                        path: 'documents-v2',
                        component: DocumentsIndexView,
                        canActivate: [PermissionGuard, FeatureGuard],
                        data: {
                            permission: [...Platform.people.employeeDocuments.permission.view],
                            canActivateRedirectTo: ['..'],
                            feature: { flags: [FeatureFlag.documentsV2] },
                        },
                    },
                    {
                        path: 'documents-v2/bulk-upload',
                        component: EmployeeDocumentsBulkUploadView,
                        canActivate: [PermissionGuard, FeatureGuard],
                        canDeactivate: [NavigationGuard],
                        data: {
                            isAccountView: false,
                            permission: [...Platform.people.employeeDocuments.permission.view],
                            canActivateRedirectTo: ['..'],
                            feature: { flags: [FeatureFlag.documentsV2] },
                        },
                    },
                    {
                        path: 'reminders',
                        component: RemindersIndexView,
                    },
                    {
                        path: 'records',
                        component: RecordsIndexView,
                    },
                    {
                        path: 'journey',
                        component: JourneyIndexView,
                    },
                    {
                        path: 'benefits',
                        component: BenefitsShowView,
                    },
                    {
                        path: 'performance',
                        component: PerformanceIndexView,
                    },
                    {
                        path: 'surveys',
                        canActivate: [PermissionGuard],
                        component: SurveyIndexView,
                        data: {
                            permission: ['accessPerformance'],
                        },
                    },
                    {
                        path: 'account',
                        component: AccountShowView,
                    },
                    {
                        path: 'time-off',
                        canActivate: [PermissionGuard],
                        component: TimeOffShowViewV3,
                        data: {
                            permission: ['manageDirectReports', 'manageDirectAndIndirectReports'],
                        },
                    },
                    {
                        path: 'time-tracking',
                        canActivate: [EmployeeModuleGuard],
                        component: TimeTrackingShow,
                        data: {
                            employeeModule: Platform.modules.timeTracking,
                        },
                    },
                    {
                        path: 'terminate',
                        component: EmployeeTerminateView,
                    },
                    {
                        path: 'leave-of-absence',
                        component: EmployeeLeaveOfAbsence,
                        canActivate: [PermissionGuard],
                        data: {
                            permission: ['manageLeaveOfAbsence'],
                        },
                        resolve: {
                            models: EmployeesResolver,
                        },
                    },
                    {
                        path: 'rehire',
                        component: EmployeeRehireView,
                    },
                    {
                        path: 'tasks',
                        component: EmployeeTasksIndexView,
                    },
                    {
                        path: 'tasks/new',
                        component: EmployeeTasksStoreView,
                    },
                    {
                        path: 'tasks/:task/edit',
                        component: EmployeeTasksStoreView,
                        resolve: {
                            models: EmployeesResolver,
                        },
                    },
                    {
                        path: 'onboarding',
                        component: OnboardingTaskIndexView,
                    },
                    {
                        path: 'onboarding/new',
                        component: OnboardingTasksStoreView,
                        canActivate: [PermissionGuard],
                        data: {
                            permission: ['task'],
                        },
                    },
                    {
                        path: 'onboarding/:task',
                        component: OnboardingTasksStoreView,
                        canActivate: [PermissionGuard],
                        data: {
                            permission: ['task'],
                        },
                        resolve: {
                            models: EmployeesResolver,
                        },
                    },
                    {
                        path: 'offboarding',
                        component: OffboardingTaskIndexView,
                    },
                    {
                        path: 'offboarding/new',
                        component: OffboardingTasksStoreView,
                        canActivate: [PermissionGuard],
                        data: {
                            permission: ['task'],
                        },
                    },
                    {
                        path: 'offboarding/:task',
                        component: OffboardingTasksStoreView,
                        canActivate: [PermissionGuard],
                        data: {
                            permission: ['task'],
                        },
                        resolve: {
                            models: EmployeesResolver,
                        },
                    },
                    {
                        path: 'training',
                        component: TrainingIndexView,
                        canActivate: [EmployeeAccessGuard],
                        data: {
                            permission: [TrainingProgram.managePermission],
                        },
                    },
                    {
                        path: 'assets',
                        component: EmployeeAssetsIndexView,
                    },
                    {
                        path: 'paystubs',
                        component: PaystubIndexView,
                        canActivate: [PermissionGuard],
                        data: {
                            permission: [...Platform.payroll.permission.view],
                        },
                    },
                ],
            },
        ],
    },
    {
        path: 'team',
        component: MyTeamView,
        canActivate: [PermissionGuard],
        data: {
            permission: [...Platform.people.myTeam.permission.view],
            canActivateRedirectTo: ['/employees/departments'],
        },
    },
    {
        path: 'organization',
        canActivate: [PermissionGuard],
        data: {
            permission: [...Platform.orgChart.permission.view],
            canActivateRedirectTo: ['/'],
        },
        children: [
            { path: '', redirectTo: 'chart', pathMatch: 'full' },
            {
                path: 'chart',
                component: OrganizationChartV2View,
                canActivate: [PermissionGuard],
                data: {
                    permission: ['view.organizationalChart'],
                },
            },
            {
                path: 'accordion',
                component: OrganizationAccordionView,
                canActivate: [PermissionGuard],
                data: { permission: ['view.organizationalChart'] },
            },
        ],
    },
    {
        path: 'offer-letters',
        loadChildren: () => import('@offer-letter/offer-letter.module').then((m) => m.OfferLetterModule),
        data: {
            permission: HireEmployee.permissions,
        },
    },

    // Company Tasks
    {
        path: 'tasks',
        component: TasksIndexView,
    },

    // Task-templates
    {
        path: 'task-templates',
        data: {
            permission: ['createAndEditTaskTemplates'],
        },
        children: [
            {
                path: '',
                component: TaskTemplatesIndexView,
            },
            {
                path: 'new',
                component: TaskTemplatesStoreView,
            },
            {
                path: ':taskTemplate/edit',
                component: TaskTemplatesStoreView,
                resolve: {
                    models: EmployeesResolver,
                },
            },
            {
                path: ':taskTemplate',
                component: TaskTemplatesShowView,
                resolve: {
                    models: EmployeesResolver,
                },
            },
        ],
    },

    {
        path: 'settings',
        children: [
            { path: '', redirectTo: 'onboarding', pathMatch: 'full' },
            {
                path: 'onboarding',
                component: OnboardingIndexView,
                canActivate: [AdminGuard],
                data: {
                    activeTab: 'settings.onboarding',
                },
            },
            {
                path: 'onboarding/tasks',
                component: OnboardingTaskTemplatesView,
                canActivate: [AdminGuard],
                data: {
                    permission: ['administrate', 'task'],
                    activeTab: 'settings.onboarding.tasks',
                },
            },
            {
                path: 'onboarding/tasks/new',
                component: OnboardingTaskTemplatesStoreView,
                canActivate: [AdminGuard],
                data: {
                    permission: ['administrate', 'task'],
                },
            },
            {
                path: 'onboarding/tasks/:taskTemplate',
                component: OnboardingTaskTemplatesStoreView,
                canActivate: [AdminGuard],
                data: {
                    permission: ['administrate', 'task'],
                },
                resolve: {
                    models: EmployeesResolver,
                },
            },
            {
                path: 'onboarding/bankingInfo',
                component: OnboardingBankingInfoView,
                canActivate: [AdminGuard],
                data: {
                    activeTab: 'settings.onboarding',
                },
            },
            {
                path: 'offboarding',
                component: OffboardingIndexView,
                canActivate: [AdminGuard],
                data: {
                    activeTab: 'settings.offboarding',
                },
            },
            {
                path: 'offboarding/tasks',
                component: OffboardingTaskTemplatesView,
                canActivate: [AdminGuard],
                data: {
                    permission: ['administrate', 'task'],
                    activeTab: 'settings.offboarding.tasks',
                },
            },
            {
                path: 'offboarding/tasks/new',
                component: OffboardingTaskTemplatesStoreView,
                canActivate: [AdminGuard],
                data: {
                    permission: ['administrate', 'task'],
                },
            },
            {
                path: 'offboarding/tasks/:taskTemplate',
                component: OffboardingTaskTemplatesStoreView,
                canActivate: [AdminGuard],
                data: {
                    permission: ['administrate', 'task'],
                },
                resolve: {
                    models: EmployeesResolver,
                },
            },
        ],
    },
];

@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
})
export class EmployeeRouterModule {}
